import { ParametersDefaults, PriorityChannel } from "./type.mjs";
import { VideoPricingMode, VideoPricingTaskType, defaults, getPriceMeter, getVideoPricingMode, isDITModelType, isMMDIT26AModelTypeStrict, isSDXLModelStrict, isVideoPricingParametersRecord, lookupVideoPerSecondPriceItem } from "./util.mjs";
import { GenerationModelTypeSubSet } from "./base.mjs";
import { EditPricing } from "./editGen.mjs";
import { DITImageGenPricing, MMDIT26AImageGenPricing, SDBasedPricing, SDXLImageGenPricing } from "./imageGen.mjs";
import { InpaintPricing } from "./inpaintGen.mjs";
import { I2VProPricing, T2I2VPricing } from "./videoGen.mjs";
import { PrepaidWorkflowGen } from "./workflowGen.mjs";
//#region src/adapter.ts
const GenerationScenario = {
	Inpaint: "inpaint",
	T2I2V: "t2i2v",
	I2VPro: "i2vPro",
	ReferenceVideo: "referenceVideo",
	Chat: "chat",
	Edit: "edit",
	PrepaidComfyWorkflow: "prepaid-comfy-workflow",
	SDImageGen: "sd-image-gen",
	SDXLImageGen: "sdxl-image-gen",
	DITV1ImageGen: "dit-v1-image-gen",
	MMDIT26AModel: "mmdit26a-image-gen",
	DiffusionModelV1: "diffusion-model-v1",
	Unknown: "unknown"
};
const DEFAULT_REFERENCE_VIDEO_INPUT_DURATION_SECONDS = 15;
function lookupReferenceVideoPerSecondPrice(config, parameters) {
	const referenceVideoPriceList = (config.videoPricing?.perSecondPriceList ?? []).filter((item) => {
		if (item.taskType !== VideoPricingTaskType.ReferenceVideo) return false;
		const itemParameters = item.parameters;
		if (!isVideoPricingParametersRecord(itemParameters)) return false;
		return "hasVideoReference" in itemParameters;
	});
	return lookupVideoPerSecondPriceItem({
		...config,
		videoPricing: {
			...config.videoPricing ?? {},
			perSecondPriceList: referenceVideoPriceList
		}
	}, VideoPricingTaskType.ReferenceVideo, parameters);
}
var PricingAdapter = class {
	parameters;
	config;
	constructor(parameters, config) {
		this.parameters = defaults(ParametersDefaults, parameters);
		this.config = defaults({
			baseStep: 20,
			baseWidth: 512,
			baseHeight: 512,
			basePrice: 400,
			maxDiscount: 800,
			samplerMultiple: {},
			sdxlMultiple: 1.25,
			sd3MediumMultiple: 1.625,
			ditMinPrice: 1e3,
			minPrice: 200,
			minPricePerSDImage: 150,
			minPricePerDitImage: 200,
			minPricePerImageForUnknownModel: 300,
			highPriority: PriorityChannel.HighPriority,
			xHighPriority: PriorityChannel.XHighPriority,
			shouldDiscount: false,
			isSDXL: false,
			isSD3Medium: false,
			modelType: ""
		}, config);
	}
	getScenario() {
		const { workflowName, t2i2v, i2vPro, referenceVideo, chat, inpaint } = this.parameters;
		const { modelType, isSDXL } = this.config;
		if (t2i2v) return GenerationScenario.T2I2V;
		else if (i2vPro) return GenerationScenario.I2VPro;
		else if (referenceVideo) return GenerationScenario.ReferenceVideo;
		else if (chat) return GenerationScenario.Edit;
		else if (inpaint) return GenerationScenario.Inpaint;
		else if (isSDXL || isSDXLModelStrict(modelType)) return GenerationScenario.SDXLImageGen;
		else if (isMMDIT26AModelTypeStrict(modelType)) return GenerationScenario.MMDIT26AModel;
		else if (isDITModelType(modelType)) return GenerationScenario.DITV1ImageGen;
		else if (workflowName?.startsWith("komiyama/inpaint-")) return GenerationScenario.Inpaint;
		else if (this.config.workflowPrepaidPrice && workflowName) return GenerationScenario.PrepaidComfyWorkflow;
		return GenerationScenario.DiffusionModelV1;
	}
	getPriceInfo() {
		const parameters = this.parameters;
		const config = this.config;
		switch (this.getScenario()) {
			case GenerationScenario.Inpaint:
				if (parameters.inpaint) return new InpaintPricing(parameters, config).getFinalPrice().toPriceInfo();
				return new SDXLImageGenPricing(parameters, {
					...config,
					modelType: GenerationModelTypeSubSet.SdxlModel
				}).getFinalPrice().toPriceInfo();
			case GenerationScenario.T2I2V: return new T2I2VPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.I2VPro: return new I2VProPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.ReferenceVideo: {
				const referenceVideoParameters = parameters.referenceVideo;
				const hasVideoReference = (referenceVideoParameters.referenceVideoMediaIds?.length ?? 0) > 0;
				if (getVideoPricingMode(config, VideoPricingTaskType.ReferenceVideo, referenceVideoParameters.model) === VideoPricingMode.PerSecond) {
					const perSecondPriceItem = lookupReferenceVideoPerSecondPrice(config, {
						model: referenceVideoParameters.model,
						mode: referenceVideoParameters.mode,
						generateAudio: referenceVideoParameters.generateAudio,
						hasVideoReference
					});
					if (perSecondPriceItem == null) {} else {
						const inputVideoDurations = referenceVideoParameters.inputVideoDurations ?? [];
						const inputVideoCount = referenceVideoParameters.referenceVideoMediaIds?.length ?? 0;
						const totalInputVideoDuration = inputVideoCount > inputVideoDurations.length ? DEFAULT_REFERENCE_VIDEO_INPUT_DURATION_SECONDS : inputVideoDurations.slice(0, inputVideoCount).reduce((sum, duration) => sum + Math.floor(duration), 0);
						const totalDuration = referenceVideoParameters.duration + totalInputVideoDuration;
						const actualTotal = Math.trunc(perSecondPriceItem.price * totalDuration);
						const configuredOriginalTotal = Math.trunc((perSecondPriceItem.originalPrice ?? perSecondPriceItem.price) * totalDuration);
						const originalTotal = Math.max(configuredOriginalTotal, actualTotal);
						const meter = getPriceMeter();
						meter.addItem((i) => i + originalTotal, {
							key: `reference-video-per-second-${referenceVideoParameters.model}-${referenceVideoParameters.mode}`,
							name: "reference video generation",
							spec: `${totalDuration}s`
						});
						if (originalTotal > actualTotal) meter.addItem((i) => i - (originalTotal - actualTotal), {
							key: `reference-video-per-second-${referenceVideoParameters.model}-${referenceVideoParameters.mode}-discount`,
							name: "reference video generation discount",
							spec: `${totalDuration}s`
						});
						return meter.toPriceInfo();
					}
				}
				return new I2VProPricing({
					...parameters,
					i2vPro: {
						model: "v3.2",
						mode: referenceVideoParameters.mode === "professional" ? "professional" : "basic",
						duration: referenceVideoParameters.duration > 5 ? "10" : "5",
						generateAudio: referenceVideoParameters.generateAudio
					}
				}, config).getFinalPrice().toPriceInfo();
			}
			case GenerationScenario.Chat:
			case GenerationScenario.Edit: return new EditPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.PrepaidComfyWorkflow: return new PrepaidWorkflowGen(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.DITV1ImageGen: return new DITImageGenPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.MMDIT26AModel: return new MMDIT26AImageGenPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.SDXLImageGen: return new SDXLImageGenPricing(parameters, config).getFinalPrice().toPriceInfo();
			case GenerationScenario.SDImageGen:
			case GenerationScenario.DiffusionModelV1:
			case GenerationScenario.Unknown:
			default: return new SDBasedPricing(parameters, config).getFinalPrice().toPriceInfo();
		}
	}
};
//#endregion
export { PricingAdapter };
